<template>
	<div
		ref="sidebar"
		class="modules-sidebar">
		<div class="modules-sidebar__title">Modules</div>
		<template
			v-for="module in app.modules"
			:key="module.name">
			<Draggable
				:ref="
					(component: unknown) =>
						setModuleRef(module.name, component as ComponentPublicInstance | null)
				"
				class="column__draggable-wrapper"
				:class="{ 'column__draggable-wrapper--readonly': readonly }"
				@dragend="onDragEndModule"
				@dragstart="onDragStartModule(module.name)">
				<button
					:key="module.icon"
					class="modules-sidebar__module"
					type="button"
					:class="{ 'modules-sidebar__module--selected': module.name === modelValue }"
					@click="emit('update:modelValue', module.name)">
					<FontAwesomeIcon
						class="modules-sidebar__module-icon"
						:icon="module.icon" />
					<span class="modules-sidebar__module-label">{{ module.displayName }}</span>
				</button>
			</Draggable>
		</template>
		<form
			v-if="newModule"
			class="modules-sidebar__new-module-container"
			@submit.prevent="saveNewModule">
			<InputText
				ref="newModuleNameInput"
				v-model="newModule.name"
				class="modules-sidebar__new-module-input"
				placeholder="Module Name"
				@input="clearValidationError"
				@keydown.enter="saveNewModule"
				@keydown.esc="cancelNewModule" />
			<div class="modules-sidebar__new-module-buttons-container">
				<button
					class="modules-sidebar__new-module-button modules-sidebar__new-module-button--cancel"
					type="button"
					@click="cancelNewModule">
					<FontAwesomeIcon icon="fal fa-xmark" />
					<span>Cancel</span>
				</button>
				<button
					class="modules-sidebar__new-module-button modules-sidebar__new-module-button--save"
					type="submit">
					<FontAwesomeIcon icon="fal fa-floppy-disk" />
					<span>Save</span>
				</button>
			</div>
		</form>
		<button
			v-if="!newModule && !readonly"
			class="modules-sidebar__module"
			type="button"
			@click="createNewModule">
			<FontAwesomeIcon
				class="modules-sidebar__module-icon"
				icon="fal fa-plus" />
			<span class="modules-sidebar__module-label">New Module</span>
		</button>
	</div>
</template>

<script setup lang="ts">
import InputText from 'primevue/inputtext'
import { nextTick, reactive, ref } from 'vue'
import type { ComponentPublicInstance, Ref } from 'vue'
import { anyToPascalCase } from '@codecannon/schemas/utils/strings'
import { useDroppable } from '@/helpers/dragAndDrop/droppable'
import Draggable from '@/helpers/dragAndDrop/Draggable.vue'
import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'
import {
	DuplicateModuleError,
	EmptyModuleNameError,
	EnrichedModule,
} from '@codecannon/schemas/schemas/EnrichedModule'
import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
	(e: 'newModule', module: EnrichedModule): void
	(e: 'update-order'): void
}>()

const newModule = ref<EnrichedModule | null>(null) as Ref<EnrichedModule | null>
const newModuleNameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)
const orderChanged = ref(false)
const draggingModule = ref<EnrichedModule | null>(null) as Ref<EnrichedModule | null>
const moduleRefs = reactive<Map<string, HTMLElement>>(new Map())
const sidebar = ref<HTMLElement>()

const props = defineProps<{
	app: EnrichedApp
	modelValue: string | null
	readonly: boolean
}>()

const { onDragEnd: onDragEndModule, onDragStart: onDragStartModule } = useDroppable({
	dropAreas: moduleRefs,
	onDragStart: (moduleName: string) => {
		draggingModule.value = props.app.modules.find((m) => m.name === moduleName)!
	},
	onAreaEnter: (moduleName: string) => {
		if (!draggingModule.value) return
		moveModuleTo(draggingModule.value, props.app.modules.find((m) => m.name === moduleName)!.order)
	},
	onDragEnd: () => {
		draggingModule.value = null
		if (orderChanged.value) {
			emit('update-order')
			orderChanged.value = false
		}
	},
})

function setModuleRef(moduleName: string, component: ComponentPublicInstance | null) {
	if (component) {
		if (component.$el instanceof HTMLElement) {
			moduleRefs.set(moduleName, component.$el)
		} else {
			moduleRefs.set(moduleName, component.$el.nextElementSibling)
		}
	} else {
		moduleRefs.delete(moduleName)
	}
}

/**
 * Moves a module to a new position in the schema
 *
 * @param module The module to move
 * @param targetOrder The new position (order) of the column
 */
function moveModuleTo(module: EnrichedModule, position: number) {
	orderChanged.value = true

	props.app.moveModule(module.name, position)
}

function createNewModule() {
	newModule.value = new EnrichedModule(
		{
			name: '',
			displayName: '',
			icon: 'fal fa-cube',
			model: {
				name: '',
				namePlural: '',
				displayName: '',
				displayNamePlural: '',
				route: '',
				table: '',
				title: '',
				subtitle: '',
				columns: {},
				relations: {},
			},
			order: props.app.modules.length - 1,
		},
		props.app,
	)
	nextTick(() => {
		newModuleNameInput.value?.$el.focus()
		sidebar.value?.scrollTo({
			top: 9999999999999,
		})
	})
}

function clearValidationError() {
	if (!newModuleNameInput.value) return
	newModuleNameInput.value.$el.setCustomValidity('')
}

function saveNewModule() {
	if (!newModule.value) return
	if (!newModuleNameInput.value) return
	newModule.value.name = anyToPascalCase(newModule.value.name)

	try {
		newModule.value.setNames(newModule.value.name, { preventAppUpdate: true })
		emit('newModule', newModule.value)
		newModule.value = null
	} catch (error) {
		if (error instanceof DuplicateModuleError || error instanceof EmptyModuleNameError) {
			newModuleNameInput.value.$el.setCustomValidity(error.message)
			newModuleNameInput.value.$el.reportValidity()
			return
		} else {
			throw error
		}
	}
}

function cancelNewModule() {
	newModule.value = null
}
</script>

<style lang="scss" scoped>
.modules-sidebar {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	min-width: 200px;
	max-width: 400px;
	overflow-y: auto;
	position: sticky;
	top: 24px;
	// Height - Headers - Container padding - Bottom padding
	max-height: calc(100dvh - 108px - 26px - 24px);
	padding-right: 8px;

	.modules-sidebar__title {
		font-size: 18px;
		width: 100%;
	}

	.modules-sidebar__new-module-container {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 8px;

		.modules-sidebar__new-module-input {
			width: 100%;
			padding: 12px 16px;
			font-size: 18px;
		}

		.modules-sidebar__new-module-buttons-container {
			display: flex;
			gap: 8px;
			width: 100%;

			& > * {
				flex: 1;
			}
		}
	}

	.modules-sidebar__module {
		width: 100%;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 16px;
		text-align: left;
		background: transparent;
		border: 1px solid transparent;
		border-radius: 6px;
		cursor: pointer;
		font-size: 15px;
		font-family: inherit;
		color: var(--p-text-secondary);
		transition:
			background-color 0.2s,
			border-color 0.2s,
			color 0.2s;

		&:hover {
			background: var(--p-surface-800);
		}

		&:active {
			background: var(--p-surface-900);
		}

		&--selected {
			background: var(--p-surface-900);
			border-color: transparent;
			color: var(--p-text-color);
		}
	}

	.modules-sidebar__module-icon {
		flex-shrink: 0;
		width: 24px;
		text-align: center;
		font-size: 18px;
	}

	.modules-sidebar__module-label {
		flex: 1;
		word-wrap: break-word;
		overflow-wrap: break-word;
		line-height: 1.4;
		text-align: left;
		font-size: 18px;
	}

	.modules-sidebar__new-module-button {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 12px;
		padding: 12px 16px;
		border: none;
		border-radius: 6px;
		cursor: pointer;
		font-size: 15px;
		font-family: inherit;
		transition:
			background-color 0.2s,
			border-color 0.2s,
			color 0.2s;

		&--cancel {
			background: var(--p-surface-900);
			color: var(--p-text-color);

			&:hover {
				background: var(--p-surface-800);
			}
		}

		&--save {
			background: var(--p-primary-color);
			border-color: var(--p-primary-color);
			color: var(--p-primary-contrast-color);

			&:hover {
				background: var(--p-primary-50);
				border-color: var(--p-primary-50);
			}

			&:active {
				background: var(--p-primary-100);
				border-color: var(--p-primary-100);
			}
		}
	}
}
</style>
